import { useEffect, useMemo, useState } from "react";
import {
  BellRing,
  CalendarClock,
  FilePenLine,
  Files,
  List,
  Plug,
  Search,
  Scale,
  Settings,
  Users,
  Workflow,
} from "lucide-react";
import { ProductShell } from "@tribunalea/ui";
import {
  APPLICATION_IDS,
  canAccessApplication,
  canAccessTool,
  TOOL_IDS,
  TRIBUNALEA_APPLICATIONS,
  TRIBUNALEA_TOOLS,
} from "@tribunalea/shared";
import {
  integrationDefinition,
} from "../integrations/registry.jsx";

const baseNavigation = [
  {
    label: "Praxis",
    items: [
      { to: "/expedientes", label: "Mis expedientes", icon: Files, toolId: TOOL_IDS.PRAXIS_EXPEDIENTES },
      { to: "/seguimientos", label: "Mis seguimientos", icon: CalendarClock, toolId: TOOL_IDS.PRAXIS_FOLLOW_UPS },
      { to: "/flujos", label: "Flujos", icon: Workflow, toolId: TOOL_IDS.PRAXIS_WORKFLOWS },
      {
        to: "/buscar-expedientes",
        label: "Buscar expedientes",
        icon: Search,
        toolId: TOOL_IDS.PRAXIS_EXPEDIENTES,
      },
      { to: "/listas", label: "Explorar listas", icon: List, toolId: TOOL_IDS.PRAXIS_DAILY_LISTS },
      { to: "/escritos", label: "Escritos y conversaciones", icon: FilePenLine, toolId: TOOL_IDS.PRAXIS_WRITING },
      {
        to: "/alertas",
        label: "Alertas",
        icon: BellRing,
        toolId: TOOL_IDS.PRAXIS_NOTIFICATIONS,
        children: [
          { to: "/alertas", label: "Configuración" },
          { to: "/alertas/historial", label: "Historial de envíos" },
        ],
      },
    ],
  },
  {
    label: "Administración",
    items: [
      { to: "/equipo", label: "Equipo", icon: Users },
      { to: "/abogados", label: "Abogados", icon: Scale, toolId: TOOL_IDS.PRAXIS_ADMINISTRATION },
      { to: "/perfil", label: "Mi perfil", icon: Settings },
      { to: "/integraciones", label: "Integraciones", icon: Plug, toolId: TOOL_IDS.PRAXIS_INTEGRATIONS },
    ],
  },
];

const portalUrl = import.meta.env.DEV
  ? "http://localhost:3000/portal"
  : "https://tribunalea.com/portal";

const accountUrl = import.meta.env.VITE_ACCOUNT_URL || (
  import.meta.env.DEV
    ? "http://localhost:5175"
    : "https://account.tribunalea.com"
);

const instrumentaUrl = import.meta.env.VITE_INSTRUMENTA_URL || (
  import.meta.env.DEV
    ? "http://localhost:5174"
    : "https://instrumenta.tribunalea.com"
);

const products = [
  {
    applicationId: APPLICATION_IDS.PRAXIS,
    name: "Praxis",
    href: import.meta.env.VITE_PRAXIS_URL || (
      import.meta.env.DEV
        ? "http://localhost:5173"
        : "https://praxis.tribunalea.com"
    ),
  },
  {
    applicationId: APPLICATION_IDS.INSTRUMENTA,
    name: "Instrumenta",
    href: instrumentaUrl,
  },
  {
    applicationId: APPLICATION_IDS.LEXIA,
    name: "Lexia",
    href: import.meta.env.VITE_LEXIA_URL || (
      import.meta.env.DEV
        ? "http://localhost:5176"
        : "https://lexia.tribunalea.com"
    ),
  },
  {
    applicationId: APPLICATION_IDS.CIVITA,
    name: "Civita",
    href: import.meta.env.VITE_CIVITA_URL || (
      import.meta.env.DEV
        ? "http://localhost:5179"
        : "https://civita.tribunalea.com"
    ),
  },
];

export function AppShell({
  access,
  accountAvatar,
  api,
  authEnabled,
  clerkControls,
  children,
}) {
  const [configuredIntegrations, setConfiguredIntegrations] = useState([]);

  useEffect(() => {
    if (!canAccessTool(access, TOOL_IDS.PRAXIS_INTEGRATIONS)) {
      setConfiguredIntegrations([]);
      return undefined;
    }
    let active = true;
    const refresh = () => {
      api.listIntegrations()
        .then((items) => {
          if (active) {
            setConfiguredIntegrations(
              items.filter((item) => item.configured),
            );
          }
        })
        .catch(() => {
          if (active) setConfiguredIntegrations([]);
        });
    };
    refresh();
    window.addEventListener("praxis:integrations-changed", refresh);
    return () => {
      active = false;
      window.removeEventListener("praxis:integrations-changed", refresh);
    };
  }, [access, api]);

  const navigation = useMemo(() => baseNavigation.map((section) => ({
    ...section,
    items: section.items.filter((item) => (
      (item.to !== "/equipo" || access?.workspace?.type === "organization")
      && (!item.toolId || canAccessTool(access, item.toolId))
    )).map((item) => {
      if (item.to !== "/integraciones") return item;
      return {
        ...item,
        children: configuredIntegrations.map((integration) => {
          const definition = integrationDefinition(integration.type);
          return {
            to: integration.route || definition?.route,
            label: integration.navigationLabel
              || definition?.navigationLabel
              || integration.name,
          };
        }).filter(({ to }) => Boolean(to)),
      };
    }),
  })).filter((section) => section.items.length), [access, configuredIntegrations]);

  const visibleProducts = products.filter((product) => (
    canAccessApplication(access, product.applicationId)
  ));
  const hiddenProductNames = TRIBUNALEA_APPLICATIONS
    .filter(({ id }) => !canAccessApplication(access, id))
    .map(({ name }) => name);
  const allowedInstrumentaToolIds = TRIBUNALEA_TOOLS
    .filter((tool) => (
      tool.applicationId === APPLICATION_IDS.INSTRUMENTA
      && !tool.id.endsWith(":assistant")
      && canAccessTool(access, tool.id)
    ))
    .map((tool) => tool.id);

  return (
    <ProductShell
      productName="Praxis"
      products={visibleProducts}
      hiddenProductNames={hiddenProductNames}
      navigation={navigation}
      instrumentaToolsUrl={canAccessApplication(
        access,
        APPLICATION_IDS.INSTRUMENTA,
      ) ? instrumentaUrl : null}
      allowedInstrumentaToolIds={allowedInstrumentaToolIds}
      portalUrl={portalUrl}
      accountSettingsHref={`${accountUrl}/creditos`}
      accountAvatar={accountAvatar}
      workspace={access?.workspace}
      accountControls={clerkControls}
      demo={!authEnabled}
    >
      {children}
    </ProductShell>
  );
}
